/**
 * Action-rule safety lookup — static imports only (browser-safe, no require()).
 */

import type { ActionRuleEntry } from './action-types';
import {
  collectPhaseBSafetyViolations,
  type WorkbenchActionSafety,
} from './workbench-action-safety-policy';
import { CORE_ACTION_RULE_TABLE } from './action-rule-table';
import { DOMAIN_ACTION_RULE_TABLE } from './domain-action-rule-table';
import { SELLABLE_GOLDEN_ACTION_RULE_TABLE } from './sellable-action-rule-table';

type SafetyLookupEntry = {
  actionId: string;
  handlerRef: string;
  safety: WorkbenchActionSafety;
};

function collectSafetyEntries(table: readonly ActionRuleEntry[]): SafetyLookupEntry[] {
  const entries: SafetyLookupEntry[] = [];
  for (const rule of table) {
    for (const action of rule.actions) {
      if (!action.safety) {
        continue;
      }
      entries.push({
        actionId: action.id,
        handlerRef: action.handlerRef,
        safety: action.safety,
      });
    }
  }
  return entries;
}

function dedupeSafetyEntriesByHandlerRef(entries: SafetyLookupEntry[]): SafetyLookupEntry[] {
  const byHandlerRef = new Map<string, SafetyLookupEntry>();
  for (const entry of entries) {
    byHandlerRef.set(entry.handlerRef, entry);
  }
  return [...byHandlerRef.values()];
}

const ACTION_SAFETY_LOOKUP: readonly SafetyLookupEntry[] = dedupeSafetyEntriesByHandlerRef([
  ...collectSafetyEntries(SELLABLE_GOLDEN_ACTION_RULE_TABLE),
  ...collectSafetyEntries(DOMAIN_ACTION_RULE_TABLE),
  ...collectSafetyEntries(CORE_ACTION_RULE_TABLE),
]);

export function countActionRuleSafetyEntries(): number {
  return ACTION_SAFETY_LOOKUP.length;
}

export function listActionRuleSafetyEntries(): readonly SafetyLookupEntry[] {
  return ACTION_SAFETY_LOOKUP;
}

export function resolveActionSafetyByActionId(actionId: string): WorkbenchActionSafety | undefined {
  return ACTION_SAFETY_LOOKUP.find((entry) => entry.actionId === actionId)?.safety;
}

export function resolveActionSafetyByHandlerRef(
  handlerRef: string
): WorkbenchActionSafety | undefined {
  return ACTION_SAFETY_LOOKUP.find((entry) => entry.handlerRef === handlerRef)?.safety;
}

const GOLDEN_PATH_RULE_TABLES = [
  { label: 'sellable', table: SELLABLE_GOLDEN_ACTION_RULE_TABLE },
  { label: 'domain', table: DOMAIN_ACTION_RULE_TABLE },
] as const;

/** Phase B — golden path sellable + domain rule tables must have full safety{} coverage. */
export function collectGoldenPathSafetyGateViolations(): string[] {
  const violations: string[] = [];

  for (const { label, table } of GOLDEN_PATH_RULE_TABLES) {
    for (const entry of table) {
      for (const action of entry.actions) {
        const actionKey = `${label}/${action.id}`;
        const tier = action.riskTier ?? action.safety?.riskTier;
        if (!tier) {
          violations.push(`${actionKey}: missing riskTier`);
        }
        if (!action.safety) {
          violations.push(`${actionKey}: missing safety{}`);
          continue;
        }
        violations.push(...collectPhaseBSafetyViolations(action.safety, actionKey));
      }
    }
  }

  return violations;
}
